import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import ReactCountryFlag from 'react-country-flag';
import { getIsoFromDialCode } from '@/utils/countryCodes';
import {
  User, Building2, ShieldCheck, FileText, Upload,
  X, Check, Download, Trash2, LayoutDashboard, LogOut,
  Bell, AlertCircle, RefreshCw, Wallet, Award, Crown, Gem,
  Clock, ChevronRight, TrendingUp, Pencil, Info, Star, Palette, ExternalLink, ShoppingCart
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { CouponRedeemSection } from '@/components/CouponRedeemSection';
import { DomainRequestModal } from '@/components/DomainRequestModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ─────────────────────────────────────────────────────────────────────────────

/* ── MSA Section Component ──────────────────────────────────────────────── */
const MsaSection = () => {
  const [msaData, setMsaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/msa/active`)
      .then(res => setMsaData(res.data))
      .catch(() => setMsaData({ active: false, document: null }))
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/msa/active/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = msaData?.document?.file_name || 'MSA-CloudSetu.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download MSA. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!msaData?.active || !msaData?.document) {
    return (
      <div className="text-center py-10">
        <FileText className="h-10 w-10 text-slate-200 mx-auto mb-3" />
        <p className="text-sm font-medium text-slate-500">No MSA Available</p>
        <p className="text-xs text-slate-400 mt-1">The Master Service Agreement has not been uploaded yet. Contact your account manager.</p>
      </div>
    );
  }

  const doc = msaData.document;
  const uploadDate = doc.uploaded_at
    ? new Date(doc.uploaded_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })
    : '—';

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">Your Master Service Agreement with CloudSetu. Please review and download for your records.</p>
      <div className="rounded-xl border border-blue-100 bg-blue-50/30 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="msa-doc-card">
        <div className="h-12 w-12 bg-white rounded-xl shadow-sm border border-blue-100 flex items-center justify-center flex-shrink-0">
          <FileText className="h-6 w-6 text-red-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-800 text-sm leading-tight break-all">{doc.file_name}</p>
          <p className="text-xs text-slate-500 mt-1">Effective Date: {uploadDate}</p>
          {doc.file_size && (
            <p className="text-xs text-slate-400 mt-0.5">
              {doc.file_size < 1024 * 1024
                ? `${(doc.file_size / 1024).toFixed(1)} KB`
                : `${(doc.file_size / (1024 * 1024)).toFixed(2)} MB`}
            </p>
          )}
        </div>
        <Button
          onClick={handleDownload}
          disabled={downloading}
          className="bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0"
          data-testid="download-msa-btn"
        >
          {downloading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          {downloading ? 'Downloading…' : 'Download MSA'}
        </Button>
      </div>
    </div>
  );
};

/* ── KYC Status Config ──────────────────────────────────────────────────── */
const KYC_STATUS_MAP = {
  in_process:          { label: 'Not Submitted',     cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  documents_submitted: { label: 'Under Review',      cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  approved:            { label: 'Approved',           cls: 'bg-green-100 text-green-700 border-green-200' },
  rejected:            { label: 'Rejected',           cls: 'bg-red-100 text-red-600 border-red-200' },
};

/* ── Category Config ────────────────────────────────────────────────────── */
const CATEGORY_CONFIG = {
  bronze:  { label: 'Bronze',  cls: 'bg-amber-100 text-amber-700 border-amber-300',   Icon: Award  },
  silver:  { label: 'Silver',  cls: 'bg-slate-100 text-slate-500 border-slate-300',   Icon: Award  },
  gold:    { label: 'Gold',    cls: 'bg-yellow-100 text-yellow-600 border-yellow-300', Icon: Crown  },
  diamond: { label: 'Diamond', cls: 'bg-cyan-100 text-cyan-600 border-cyan-300',       Icon: Gem    },
};

const NEXT_TIER = {
  bronze:  { next: 'silver',  clients: 10, cps: 2  },
  silver:  { next: 'gold',    clients: 30, cps: 5  },
  gold:    { next: 'diamond', clients: 50, cps: 10  },
  diamond: null,
};

/* ── Document type lists ────────────────────────────────────────────────── */
const ID_PROOF_TYPES = [
  'Business Commencement Certificate',
  'Certificate of Registration',
  "Declaration on the letter head enlisting all partners' names with their inclusion date",
  'Domicile certificate with address issued by municipal corporation',
  'Duly executed partnership deed (as amended and updated up to date)',
  'GST registration certificate',
  'MSME certificate (if registered)',
  'PAN Card',
  'Registered deed (as amended and updated up to date)',
  'Registration Certificate',
  'Registration Certificate/Notification/Circular/Gazette for institution formation',
  'Registration Certificate (if any) under Banking Companies Act 1949',
  'ROC issued CIN',
  'SEBI registration certificate',
  'Shops and Establishment Certificate',
  'Shops and Establishment Certificate issued by local self Govt',
];
const ADDRESS_PROOF_TYPES = [
  'Current registered lease or license agreement along with utility bill',
  'Electricity bill',
  'GST registration certificate',
  'MTNL-BSNL phone line bill',
  'Property tax bill',
  'Water bill',
];

/* ── Helper Components ──────────────────────────────────────────────────── */
const KYCBadge = ({ status }) => {
  const m = KYC_STATUS_MAP[status] || KYC_STATUS_MAP.in_process;
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${m.cls}`}>
      {m.label}
    </span>
  );
};

const DocStatusBadge = ({ status }) => {
  const map = {
    in_process: { cls: 'bg-amber-100 text-amber-700 border-amber-200', label: 'In Review' },
    approved:   { cls: 'bg-green-100 text-green-700 border-green-200', label: 'Approved' },
    rejected:   { cls: 'bg-red-100 text-red-600 border-red-200',       label: 'Rejected' },
  };
  const m = map[status] || map.in_process;
  return <span className={`px-2 py-0.5 rounded border text-[10px] font-semibold ${m.cls}`}>{m.label}</span>;
};

const CategoryBadge = ({ category }) => {
  const c = CATEGORY_CONFIG[category?.toLowerCase()] || CATEGORY_CONFIG.bronze;
  const Icon = c.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${c.cls}`}>
      <Icon className="h-3.5 w-3.5" /> {c.label}
    </span>
  );
};

/* ── Document Category Form Section ────────────────────────────────────── */
const DocCategorySection = ({ title, badge, types, state, onChange, error }) => {
  return (
  <div className={`border rounded-xl p-5 ${error ? 'border-red-300 bg-red-50/30' : 'border-slate-200 bg-white'}`}>
    <div className="flex items-center gap-2 mb-4">
      <span className="bg-blue-600 text-white text-xs font-bold rounded-md px-2 py-0.5">{title}</span>
      <span className="text-red-500 text-xs ml-1">*Required</span>
    </div>
    <div className="space-y-3">
      {/* Document Type — Radix UI Select for full mobile/tablet compatibility */}
      <div>
        <label className="text-xs font-medium text-slate-600 block mb-1.5">
          Document Type <span className="text-red-500">*</span>
        </label>
        <Select value={state.doc_type} onValueChange={v => onChange({ ...state, doc_type: v })}>
          <SelectTrigger
            className={`w-full h-10 text-sm bg-white ${error?.doc_type ? 'border-red-400' : 'border-slate-200'}`}
            data-testid={`${title.toLowerCase().replace(/\s+/g,'-')}-doc-type`}
          >
            <SelectValue placeholder="Select document type..." />
          </SelectTrigger>
          <SelectContent position="popper" className="z-[9999]">
            {types.map(t => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {error?.doc_type && <p className="text-xs text-red-500 mt-1">{error.doc_type}</p>}
      </div>
      {/* Document Name */}
      <div>
        <label className="text-xs font-medium text-slate-600 block mb-1.5">
          Document Name <span className="text-red-500">*</span>
        </label>
        <Input
          value={state.doc_name}
          onChange={e => onChange({ ...state, doc_name: e.target.value })}
          placeholder="e.g. PAN Card of ABC Pvt Ltd"
          className={error?.doc_name ? 'border-red-400' : ''}
        />
        {error?.doc_name && <p className="text-xs text-red-500 mt-1">{error.doc_name}</p>}
      </div>
      {/* File Upload */}
      <div>
        <label className="text-xs font-medium text-slate-600 block mb-1.5">
          Upload File <span className="text-red-500">*</span>
        </label>
        {state.file ? (
          <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <FileText className="h-4 w-4 text-blue-600 flex-shrink-0" />
            <span className="text-sm text-blue-800 truncate flex-1">{state.file.name}</span>
            <button onClick={() => onChange({ ...state, file: null })} className="text-slate-400 hover:text-red-500 flex-shrink-0">
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <label className={`cursor-pointer block p-4 border-2 border-dashed rounded-lg text-center hover:border-blue-400 hover:bg-blue-50/50 transition-colors ${error?.file ? 'border-red-400 bg-red-50/20' : 'border-slate-300'}`}>
            <Upload className="h-5 w-5 text-slate-400 mx-auto mb-1" />
            <span className="text-xs text-slate-500">Click to upload (PDF, JPG, PNG)</span>
            <input
              type="file"
              hidden
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={e => { const f = e.target.files?.[0]; if (f) onChange({ ...state, file: f }); e.target.value = ''; }}
            />
          </label>
        )}
        {error?.file && <p className="text-xs text-red-500 mt-1">{error.file}</p>}
      </div>
    </div>
  </div>
  );
};

/* ── Main Component ─────────────────────────────────────────────────────── */
export default function KYCPortal() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout } = useAuth();

  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'profile');
  const [docsSubTab, setDocsSubTab] = useState('msa');

  /* ── Profile ── */
  const [profile, setProfile] = useState({ full_name: '', email: '', mobile: '', mobile_country_code: '+91', role: '' });

  /* ── Account ── */
  const [account, setAccount] = useState({
    account_name: '', account_type: '', crm_id: '', account_status: 'pending',
    account_category: 'bronze', industry_group: '', sub_vertical: '',
    pan: '', tan: '', duns: '', website: '', gst_number: '', joining_fee: 0,
  });

  /* ── KYC ── */
  const [kycData, setKycData] = useState({ kyc_status: 'in_process', documents: [] });
  const [idProof, setIdProof] = useState({ doc_type: '', doc_name: '', file: null });
  const [addrProof, setAddrProof] = useState({ doc_type: '', doc_name: '', file: null });
  const [kycErrors, setKycErrors] = useState({});
  const [submittingKYC, setSubmittingKYC] = useState(false);

  /* ── Per-document resubmission ── */
  const [resubmitOpen, setResubmitOpen] = useState({ id_proof: false, address_proof: false });
  const [resubmitState, setResubmitState] = useState({
    id_proof: { doc_type: '', doc_name: '', file: null },
    address_proof: { doc_type: '', doc_name: '', file: null },
  });
  const [resubmitErrors, setResubmitErrors] = useState({ id_proof: {}, address_proof: {} });
  const [submittingResubmit, setSubmittingResubmit] = useState({ id_proof: false, address_proof: false });

  /* ── Wallet / Counts ── */
  const [walletInfo, setWalletInfo] = useState({ balance: 0, welcome_bonus: 0, account_category: 'bronze' });
  const [portalCounts, setPortalCounts] = useState({ direct_clients: 0, direct_channel_partners: 0 });

  /* ── White Label ── */
  // ── Domain state ─────────────────────────────────────────────────────────
  const [domainOption, setDomainOption] = useState(null); // 'subdomain' | 'custom' | 'buy'
  const [domainInput, setDomainInput] = useState('');
  const [domainLoading, setDomainLoading] = useState(false);

  // Domain Request Modal state
  const [modalOpen, setModalOpen]             = useState(false);
  const [pendingDomain, setPendingDomain]     = useState('');
  const [pendingPrice, setPendingPrice]       = useState(0);
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [domainMsg, setDomainMsg] = useState(null); // {type:'success'|'error'|'info', text}
  const [dnsRecords, setDnsRecords] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [wlUploading, setWlUploading] = useState(false);
  const wlFileInputRef = useRef(null);
  const [wlSettings, setWlSettings] = useState(null);
  const [wlForm, setWlForm] = useState({ primary_color: '#0f172a', accent_color: '#3b82f6', show_pricing: true });
  const [wlSaving, setWlSaving] = useState(false);

  /* ── Notifications ── */
  const [notifications, setNotifications] = useState([]);
  const [showNotifDrop, setShowNotifDrop] = useState(false);

  const kycApproved = kycData.kyc_status === 'approved';

  const fetchData = useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        axios.get(`${API}/partner/profile`),
        axios.get(`${API}/partner/account`),
        axios.get(`${API}/partner/kyc/status`),
        axios.get(`${API}/partner/portal/wallet`),
        axios.get(`${API}/partner/portal/counts`),
        axios.get(`${API}/notifications`),
      ]);
      if (results[0].status === 'fulfilled') setProfile(results[0].value.data);
      if (results[1].status === 'fulfilled') setAccount(results[1].value.data);
      if (results[2].status === 'fulfilled') setKycData(results[2].value.data);
      if (results[3].status === 'fulfilled') setWalletInfo(results[3].value.data);
      if (results[4].status === 'fulfilled') setPortalCounts(results[4].value.data);
      if (results[5].status === 'fulfilled') setNotifications(results[5].value.data);
    } catch {}
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Re-fetch wallet info after any credit event (coupon, topup, KYC bonus)
  useEffect(() => {
    const refreshWallet = async () => {
      try {
        const res = await axios.get(`${API}/partner/portal/wallet`);
        setWalletInfo(res.data);
      } catch {}
    };
    window.addEventListener('cloudsetu:wallet-updated', refreshWallet);
    return () => window.removeEventListener('cloudsetu:wallet-updated', refreshWallet);
  }, []);
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  /* ── Lazy-load White Label settings when tab opens ── */
  useEffect(() => {
    if (activeTab !== 'white_label' || wlSettings !== null) return;
    axios.get(`${API}/white-label/settings`)
      .then(r => {
        setWlSettings(r.data);
        setWlForm({
          primary_color: r.data.primary_color || '#0f172a',
          accent_color: r.data.accent_color || '#3b82f6',
          show_pricing: r.data.show_pricing !== false,
        });
      })
      .catch(() => {});
  }, [activeTab, wlSettings]);

  const handleWlSave = async () => {
    setWlSaving(true);
    try {
      await axios.put(`${API}/white-label/settings`, wlForm);
      toast.success('White Label settings saved');
      // Refresh
      const r = await axios.get(`${API}/white-label/settings`);
      setWlSettings(r.data);
    } catch { toast.error('Failed to save settings'); }
    finally { setWlSaving(false); }
  };

  const handleWlLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/png','image/jpeg','image/jpg','image/gif','image/webp'];
    if (!allowed.includes(file.type)) { toast.error('Invalid file type'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Max 2MB'); return; }
    setWlUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      await axios.post(`${API}/white-label/upload-logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Logo uploaded');
      const r = await axios.get(`${API}/white-label/settings`);
      setWlSettings(r.data);
      setWlForm(f => ({ ...f, logo_url: r.data.logo_url }));
    } catch { toast.error('Upload failed'); }
    finally { setWlUploading(false); if (wlFileInputRef.current) wlFileInputRef.current.value = ''; }
  };

  const handleWlDeleteLogo = async () => {
    try {
      await axios.delete(`${API}/white-label/delete-logo`);
      toast.success('Logo removed');
      setWlSettings(s => ({ ...s, logo_url: null }));
      setWlForm(f => ({ ...f, logo_url: '' }));
    } catch { toast.error('Failed to remove logo'); }
  };

  // ── Domain handlers ──────────────────────────────────────────────────────
  const handleSetSubdomain = async () => {
    if (!domainInput.trim()) return;
    setDomainLoading(true); setDomainMsg(null);
    try {
      const res = await axios.put(`${API}/white-label/domain/subdomain`, { subdomain: domainInput.trim().toLowerCase() });
      setDomainMsg({ type: 'success', text: `✅ Activated! Your storefront: ${res.data.url}` });
      setWlSettings(s => ({ ...s, subdomain: res.data.subdomain, domain_type: 'subdomain', domain_status: 'active' }));
      window.dispatchEvent(new CustomEvent('cloudsetu:wallet-updated'));
    } catch (e) {
      setDomainMsg({ type: 'error', text: e.response?.data?.detail || 'Failed to set subdomain' });
    } finally { setDomainLoading(false); }
  };

  const handleSetCustomDomain = async () => {
    if (!domainInput.trim()) return;
    setDomainLoading(true); setDomainMsg(null); setDnsRecords(null);
    try {
      const res = await axios.put(`${API}/white-label/domain/custom`, { domain: domainInput.trim() });
      setDnsRecords(res.data.dns_records);
      setDomainMsg({ type: 'info', text: res.data.message });
      setWlSettings(s => ({ ...s, custom_domain: res.data.domain, domain_type: 'custom', domain_status: 'pending_verification' }));
    } catch (e) {
      setDomainMsg({ type: 'error', text: e.response?.data?.detail || 'Failed to save domain' });
    } finally { setDomainLoading(false); }
  };

  const handleVerifyDNS = async () => {
    setVerifying(true); setDomainMsg(null);
    try {
      const res = await axios.post(`${API}/white-label/domain/verify`);
      if (res.data.verified) {
        setDomainMsg({ type: 'success', text: res.data.message });
        setWlSettings(s => ({ ...s, domain_status: 'verified' }));
        setDnsRecords(null);
      } else {
        setDomainMsg({ type: 'error', text: res.data.message });
      }
    } catch (e) {
      setDomainMsg({ type: 'error', text: e.response?.data?.detail || 'Verification failed' });
    } finally { setVerifying(false); }
  };

  const handleSearchDomains = async () => {
    if (!domainInput.trim()) return;
    setSearching(true); setSearchResults(null); setDomainMsg(null);
    try {
      const res = await axios.get(`${API}/white-label/domain/search`, { params: { name: domainInput.trim() } });
      setSearchResults(res.data.domains || []);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const isNetworkError = !e.response || e.code === 'ERR_NETWORK';
      const msg = isNetworkError
        ? 'Domain search requires internet access to ResellerClub. This works on the live production domain.'
        : (detail || 'Domain search is unavailable right now. This feature works on the live production domain.');
      setDomainMsg({ type: 'error', text: msg });
    } finally { setSearching(false); }
  };

  const handleBuyDomain = (domain, price) => {
    setPendingDomain(domain);
    setPendingPrice(price || 0);
    setModalOpen(true);
  };

  const handleModalSubmit = async (payload) => {
    setModalSubmitting(true);
    try {
      await axios.post(`${API}/domain/request`, payload);
      setModalOpen(false);
      setSearchResults(null);
      setDomainMsg({
        type: 'success',
        text: "Your domain request has been submitted. You'll be notified once it's approved.",
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Request submission failed');
    } finally {
      setModalSubmitting(false);
    }
  };


  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-all-read`);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch {}
  };

  const handleDashboard = () => {
    if (!kycApproved) { toast.info('Your KYC is not yet approved. Please wait for admin verification.'); return; }
    navigate(user?.role === 'sub_reseller' ? '/sub-reseller/dashboard' : '/reseller/dashboard');
  };

  /* ── KYC Submission ── */
  const validateKYC = () => {
    const e = {};
    const id = {};
    const ad = {};
    if (!idProof.doc_type) id.doc_type = 'Select a document type';
    if (!idProof.doc_name.trim()) id.doc_name = 'Enter document name';
    if (!idProof.file) id.file = 'Upload the document file';
    if (!addrProof.doc_type) ad.doc_type = 'Select a document type';
    if (!addrProof.doc_name.trim()) ad.doc_name = 'Enter document name';
    if (!addrProof.file) ad.file = 'Upload the document file';
    if (Object.keys(id).length > 0) e.id_proof = id;
    if (Object.keys(ad).length > 0) e.address_proof = ad;
    return e;
  };

  const handleSubmitKYC = async () => {
    const e = validateKYC();
    if (Object.keys(e).length > 0) { setKycErrors(e); return; }
    setSubmittingKYC(true);
    try {
      const formData = new FormData();
      formData.append('id_proof_type', idProof.doc_type);
      formData.append('id_proof_name', idProof.doc_name);
      formData.append('id_proof_file', idProof.file);
      formData.append('address_proof_type', addrProof.doc_type);
      formData.append('address_proof_name', addrProof.doc_name);
      formData.append('address_proof_file', addrProof.file);
      const res = await axios.post(`${API}/partner/kyc/submit-verification`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('KYC documents submitted for verification');
      setKycData(prev => ({ ...prev, kyc_status: res.data.kyc_status || 'documents_submitted' }));
      await fetchData();
      setIdProof({ doc_type: '', doc_name: '', file: null });
      setAddrProof({ doc_type: '', doc_name: '', file: null });
      setKycErrors({});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally { setSubmittingKYC(false); }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(`${API}/partner/kyc/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = doc.filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const handleDeleteDoc = async (docId) => {
    try {
      await axios.delete(`${API}/partner/kyc/documents/${docId}`);
      setKycData(prev => ({ ...prev, documents: prev.documents.filter(d => d.id !== docId) }));
      toast.success('Document removed');
    } catch { toast.error('Failed to delete'); }
  };

  /* ── Per-document Resubmission ── */
  const handleResubmitDoc = async (category) => {
    const state = resubmitState[category];
    const errors = {};
    if (!state.doc_type) errors.doc_type = 'Select a document type';
    if (!state.file) errors.file = 'Upload the document file';
    if (Object.keys(errors).length > 0) {
      setResubmitErrors(prev => ({ ...prev, [category]: errors }));
      return;
    }
    setSubmittingResubmit(prev => ({ ...prev, [category]: true }));
    try {
      const formData = new FormData();
      formData.append('category', category);
      formData.append('doc_type', state.doc_type);
      formData.append('doc_name', state.doc_name || '');
      formData.append('file', state.file);
      await axios.post(`${API}/partner/kyc/resubmit-document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${category === 'id_proof' ? 'ID Proof' : 'Address Proof'} resubmitted for review`);
      setResubmitOpen(prev => ({ ...prev, [category]: false }));
      setResubmitState(prev => ({ ...prev, [category]: { doc_type: '', doc_name: '', file: null } }));
      setResubmitErrors(prev => ({ ...prev, [category]: {} }));
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Resubmission failed');
    } finally {
      setSubmittingResubmit(prev => ({ ...prev, [category]: false }));
    }
  };

  /* ── Computed ── */
  const currentCat = (walletInfo.account_category || account.account_category || 'bronze').toLowerCase();
  const nextTier = NEXT_TIER[currentCat];
  const unreadCount = notifications.filter(n => !n.is_read).length;
  const submittedDocs = kycData.documents || [];
  const latestIdProof = [...submittedDocs].filter(d => d.category === 'id_proof').sort((a, b) => b.uploaded_at > a.uploaded_at ? 1 : -1)[0];
  const latestAddrProof = [...submittedDocs].filter(d => d.category === 'address_proof').sort((a, b) => b.uploaded_at > a.uploaded_at ? 1 : -1)[0];
  const hasRejectedDocs = submittedDocs.some(d => d.doc_status === 'rejected');

  const NAV_TABS = [
    { id: 'profile',     label: 'User Profile',    Icon: User },
    { id: 'account',     label: 'Account Details',  Icon: Building2 },
    { id: 'kyc',         label: 'KYC',              Icon: ShieldCheck },
    { id: 'documents',   label: 'Documents',        Icon: FileText },
    { id: 'white_label', label: 'White Label',      Icon: Palette },
    { id: 'freebies',     label: 'Freebies',         Icon: Star },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ── Header ── */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <ShieldCheck className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-800 leading-tight">Partner Portal</p>
                <p className="text-[10px] text-slate-400 leading-tight">KYC Verification</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Welcome Bonus + Category — hidden for Channel Partners */}
              {user?.role !== 'sub_reseller' && (<>
                <div className="hidden sm:flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5" data-testid="portal-wallet-badge">
                  <Wallet className="h-4 w-4 text-blue-600" />
                  <div>
                    <p className="text-[9px] text-blue-500 font-medium leading-tight">Wallet Balance</p>
                    <p className="text-sm font-bold text-blue-800 leading-tight">₹{(walletInfo.balance || 0).toLocaleString('en-IN')}</p>
                  </div>
                </div>
                <div className="hidden sm:block">
                  <CategoryBadge category={currentCat} />
                </div>
              </>)}

              {/* Dashboard Button */}
              <Button
                onClick={handleDashboard}
                data-testid="go-to-dashboard-btn"
                disabled={!kycApproved}
                size="sm"
                className={`flex items-center gap-2 text-xs transition-all ${
                  kycApproved ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
                title={kycApproved ? 'Open Dashboard' : 'KYC approval required'}
              >
                <LayoutDashboard className="h-3.5 w-3.5" /> Go to Dashboard
              </Button>

              {/* Notification Bell */}
              <div className="relative">
                <button
                  onClick={() => setShowNotifDrop(v => !v)}
                  className="relative p-2 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                  data-testid="notif-bell"
                >
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </button>
                {showNotifDrop && (
                  <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl border border-slate-200 shadow-xl z-50">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                      <span className="text-sm font-semibold text-slate-800">Notifications</span>
                      <div className="flex items-center gap-2">
                        {unreadCount > 0 && (
                          <button onClick={markAllRead} className="text-xs text-blue-600 hover:underline">Mark all read</button>
                        )}
                        <button onClick={() => setShowNotifDrop(false)} className="text-slate-400 hover:text-slate-600">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <p className="px-4 py-6 text-sm text-slate-400 text-center">No notifications</p>
                      ) : notifications.slice(0, 10).map(n => (
                        <div key={n.id} className={`px-4 py-3 border-b last:border-0 ${!n.is_read ? 'bg-blue-50/60' : 'hover:bg-slate-50'}`}>
                          <div className="flex items-start gap-2">
                            {n.type?.includes('rejected') ? (
                              <AlertCircle className="h-3.5 w-3.5 text-red-500 mt-0.5 flex-shrink-0" />
                            ) : (
                              <Check className="h-3.5 w-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                            )}
                            <div>
                              <p className={`text-xs leading-snug ${!n.is_read ? 'font-medium text-slate-800' : 'text-slate-600'}`}>{n.message}</p>
                              <p className="text-[10px] text-slate-400 mt-0.5">{new Date(n.created_at).toLocaleString('en-IN')}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Logout */}
              <Button variant="ghost" size="sm" className="text-slate-500 hover:text-red-600"
                onClick={() => { logout(); navigate('/login'); }} data-testid="kyc-portal-logout">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ── KYC Not-Approved Banner ── */}
      {!kycApproved && (
        <div className={`border-b ${kycData.kyc_status === 'documents_submitted' ? 'bg-blue-50 border-blue-200' : kycData.kyc_status === 'rejected' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
            {kycData.kyc_status === 'documents_submitted' ? (
              <>
                <Clock className="h-4 w-4 text-blue-600 flex-shrink-0" />
                <p className="text-xs text-blue-700">
                  <strong>Documents under review.</strong> Our team will verify your KYC soon. You'll be notified once approved.
                </p>
              </>
            ) : kycData.kyc_status === 'rejected' ? (
              <>
                <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                <p className="text-xs text-red-700">
                  <strong>KYC Rejected.</strong>{kycData.rejection_reason ? ` Reason: ${kycData.rejection_reason}` : ''} Please review and resubmit your documents.
                </p>
              </>
            ) : (
              <>
                <Info className="h-4 w-4 text-amber-600 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  <strong>KYC Verification required.</strong> Complete your profile, account details, and submit KYC documents to access the full dashboard.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Main Layout ── */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 flex gap-6">

        {/* ── Left Nav ── */}
        <div className="w-52 flex-shrink-0">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {/* User info */}
            <div className="px-4 py-4 border-b border-slate-100 bg-slate-50">
              <div className="h-10 w-10 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center mb-2">
                {(profile.full_name || user?.email || 'P').charAt(0).toUpperCase()}
              </div>
              <p className="text-sm font-semibold text-slate-800 truncate">{profile.full_name || account.account_name || user?.email}</p>
              <p className="text-xs text-slate-400 truncate mt-0.5">{user?.email}</p>
              <div className="mt-2">
                {user?.role !== 'sub_reseller' && <CategoryBadge category={currentCat} />}
              </div>
            </div>
            {/* Nav items */}
            <nav className="p-1.5">
              {NAV_TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                  }`}
                  data-testid={`nav-${tab.id}`}
                >
                  <tab.Icon className="h-4 w-4 flex-shrink-0" />
                  {tab.label}
                  {tab.id === 'kyc' && (
                    <span className={`ml-auto w-2 h-2 rounded-full ${
                      kycData.kyc_status === 'approved' ? 'bg-green-500' :
                      kycData.kyc_status === 'documents_submitted' ? 'bg-blue-400' :
                      kycData.kyc_status === 'rejected' ? 'bg-red-500' : 'bg-amber-400'
                    }`} />
                  )}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* ── Content Area ── */}
        <div className="flex-1 min-w-0 space-y-4">

          {/* ══ USER PROFILE TAB ══════════════════════════════════════════════ */}
          {activeTab === 'profile' && (
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-slate-800">User Profile</h2>
                  <p className="text-xs text-slate-500 mt-0.5">Your personal information</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => navigate('/partner/edit-profile')}
                  className="text-blue-600 border-blue-200 hover:bg-blue-600 hover:border-blue-600 flex items-center gap-2"
                  data-testid="edit-profile-btn">
                  <Pencil className="h-3.5 w-3.5" /> Edit Profile
                </Button>
              </div>
              <div className="divide-y divide-slate-100 px-6">
                {[
                  { label: 'Full Name', value: profile.full_name },
                  { label: 'Email', value: profile.email, mono: false },
                  { label: 'Role', value: profile.role === 'reseller' ? 'Synergy Partner' : profile.role === 'sub_reseller' ? 'Channel Partner' : profile.role },
                ].map(f => (
                  <div key={f.label} className="flex items-center py-3.5 gap-6">
                    <span className="w-40 flex-shrink-0 text-sm font-medium text-slate-500">{f.label}</span>
                    <span className={`text-sm text-slate-800 ${f.mono ? 'font-mono' : ''}`}>{f.value || '—'}</span>
                  </div>
                ))}
                {/* Mobile — inline flag */}
                <div className="flex items-center py-3.5 gap-6">
                  <span className="w-40 flex-shrink-0 text-sm font-medium text-slate-500">Mobile</span>
                  <span className="text-sm text-slate-800 flex items-center gap-2">
                    {profile.mobile_country_code && getIsoFromDialCode(profile.mobile_country_code) && (
                      <ReactCountryFlag
                        countryCode={getIsoFromDialCode(profile.mobile_country_code)}
                        svg style={{ width: '1.2em', height: '1.2em', borderRadius: '2px' }}
                        title={profile.mobile_country_code}
                      />
                    )}
                    {`${profile.mobile_country_code || ''} ${profile.mobile || ''}`.trim() || '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* ── Coupon Redemption ────────────────────────────── */}
            <CouponRedeemSection />
          </div>
          )}

          {/* ══ ACCOUNT DETAILS TAB ══════════════════════════════════════════ */}
          {activeTab === 'account' && (
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <h2 className="font-semibold text-slate-800">Account Details</h2>
                    <p className="text-xs text-slate-500 mt-0.5">Business and account information</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => navigate('/partner/edit-account')}
                    className="text-blue-600 border-blue-200 hover:bg-blue-600 hover:border-blue-600 flex items-center gap-2"
                    data-testid="edit-account-btn">
                    <Pencil className="h-3.5 w-3.5" /> Edit Account Details
                  </Button>
                </div>
                <div className="divide-y divide-slate-100 px-6">
                  {/* Account */}
                  <div className="py-3 flex items-center gap-6">
                    <span className="w-40 flex-shrink-0 text-xs font-semibold text-slate-400 uppercase tracking-wide">General</span>
                  </div>
                  {[
                    { label: 'Account Name',     value: account.account_name },
                    { label: 'Account Type',     value: account.account_type },
                    { label: 'Account ID',       value: account.account_id, mono: true },
                    { label: 'CRM ID',           value: account.crm_id, mono: true },
                    { label: 'Account Status',   value: account.account_status ? account.account_status.charAt(0).toUpperCase() + account.account_status.slice(1) : '—' },
                    ...(user?.role !== 'sub_reseller' ? [{ label: 'Account Category', value: null, category: currentCat }] : []),
                  ].map(f => (
                    <div key={f.label} className="flex items-center py-3 gap-6">
                      <span className="w-40 flex-shrink-0 text-sm font-medium text-slate-500">{f.label}</span>
                      {f.category
                        ? <CategoryBadge category={f.category} />
                        : <span className={`text-sm text-slate-800 ${f.mono ? 'font-mono text-xs' : ''}`}>{f.value || '—'}</span>
                      }
                    </div>
                  ))}
                  {/* Industry */}
                  <div className="py-3 flex items-center gap-6">
                    <span className="w-40 flex-shrink-0 text-xs font-semibold text-slate-400 uppercase tracking-wide">Industry</span>
                  </div>
                  {[
                    { label: 'Industry Group',  value: account.industry_group },
                    { label: 'Sub-Vertical',    value: account.sub_vertical },
                    { label: 'GST Number',      value: account.gst_number, mono: true },
                    { label: 'Website',         value: account.website },
                  ].map(f => (
                    <div key={f.label} className="flex items-center py-3 gap-6">
                      <span className="w-40 flex-shrink-0 text-sm font-medium text-slate-500">{f.label}</span>
                      <span className={`text-sm text-slate-800 ${f.mono ? 'font-mono text-xs' : ''}`}>{f.value || '—'}</span>
                    </div>
                  ))}
                  {/* Tax IDs */}
                  <div className="py-3 flex items-center gap-6">
                    <span className="w-40 flex-shrink-0 text-xs font-semibold text-slate-400 uppercase tracking-wide">Tax & Regulatory</span>
                  </div>
                  {[
                    { label: 'PAN Number', value: account.pan ? '****' + account.pan.slice(-4) : '—', mono: true },
                    { label: 'TAN Number', value: account.tan, mono: true },
                    { label: 'DUNS Number', value: account.duns, mono: true },
                  ].map(f => (
                    <div key={f.label} className="flex items-center py-3 gap-6">
                      <span className="w-40 flex-shrink-0 text-sm font-medium text-slate-500">{f.label}</span>
                      <span className={`text-sm text-slate-800 ${f.mono ? 'font-mono text-xs' : ''}`}>{f.value || '—'}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Category Progression Card — hidden for Channel Partners */}
              {user?.role !== 'sub_reseller' && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="h-4 w-4 text-blue-600" />
                  <h3 className="text-sm font-semibold text-slate-800">Account Category Progression</h3>
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <CategoryBadge category={currentCat} />
                  {nextTier && (
                    <>
                      <ChevronRight className="h-4 w-4 text-slate-300" />
                      <CategoryBadge category={nextTier.next} />
                    </>
                  )}
                </div>
                {nextTier ? (
                  <div className="space-y-3">
                    <p className="text-xs text-slate-600">
                      To reach <strong className="capitalize">{nextTier.next}</strong>, you need:
                    </p>
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-xs text-slate-500">Direct Clients</span>
                        <span className="text-xs font-medium text-slate-700">{portalCounts.direct_clients} / {nextTier.clients}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full transition-all"
                          style={{ width: `${Math.min(100, (portalCounts.direct_clients / nextTier.clients) * 100)}%` }} />
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 text-center">(Optional)</p>
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-xs text-slate-500">Channel Partners</span>
                        <span className="text-xs font-medium text-slate-700">{portalCounts.direct_channel_partners} / {nextTier.cps}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full transition-all"
                          style={{ width: `${Math.min(100, (portalCounts.direct_channel_partners / nextTier.cps) * 100)}%` }} />
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">Tiers: Bronze → Silver (10 clients) → Gold (30 clients) → Diamond (50 clients)</p>
                  </div>
                ) : (
                  <p className="text-sm text-cyan-700 font-medium">You've reached the highest tier!</p>
                )}
              </div>
              )}
            </div>
          )}

          {/* ══ KYC TAB ══════════════════════════════════════════════════════ */}
          {activeTab === 'kyc' && (
            <div className="space-y-4">
              {/* Status Card */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 mb-2">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-semibold text-slate-800">KYC Verification Status</h2>
                  <KYCBadge status={kycData.kyc_status} />
                </div>
                <p className="text-xs text-slate-500">
                  {kycData.kyc_status === 'approved' && 'Your KYC is approved. You have full dashboard access.'}
                  {kycData.kyc_status === 'documents_submitted' && 'Your documents are submitted and awaiting admin review. You will be notified when verified.'}
                  {kycData.kyc_status === 'in_process' && 'Please submit your ID Proof and Address Proof documents below to begin verification.'}
                  {kycData.kyc_status === 'rejected' && 'Your KYC was rejected. Review the feedback below and resubmit the rejected documents.'}
                </p>
              </div>

              {/* Approved State */}
              {kycApproved && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-5 flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Check className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-green-800">KYC Verified</p>
                      {kycData.verified_at && (
                        <p className="text-xs text-green-600 mt-0.5">Approved on {new Date(kycData.verified_at).toLocaleDateString('en-IN')}</p>
                      )}
                    </div>
                  </div>
                  <Button onClick={handleDashboard} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="kyc-go-to-dashboard">
                    <LayoutDashboard className="h-4 w-4 mr-2" /> Go to Dashboard
                  </Button>
                </div>
              )}

              {/* Documents submitted — per-document status view */}
              {(kycData.kyc_status === 'documents_submitted' || kycData.kyc_status === 'rejected') && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-sm font-semibold text-slate-800">Submitted Documents</h3>
                    <p className="text-xs text-slate-500">Each document is reviewed individually</p>
                  </div>

                  {[
                    { category: 'id_proof', label: 'ID Proof', doc: latestIdProof, types: ID_PROOF_TYPES },
                    { category: 'address_proof', label: 'Address Proof', doc: latestAddrProof, types: ADDRESS_PROOF_TYPES },
                  ].map(({ category, label, doc, types }) => {
                    const isRejected = doc?.doc_status === 'rejected';
                    const isApproved = doc?.doc_status === 'approved';
                    const open = resubmitOpen[category];

                    return (
                      <div key={category} className="space-y-2 mb-2">
                        {/* Status card */}
                        <div className={`rounded-xl border p-4 transition-colors mb-2 ${
                          isRejected  ? 'border-red-200 bg-red-50/30' :
                          isApproved  ? 'border-green-200 bg-green-50/20' :
                          'border-slate-200 bg-white'
                        }`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="text-xs font-semibold text-slate-500">{label}</span>
                                {doc && <DocStatusBadge status={doc.doc_status || 'in_process'} />}
                                {!doc && <span className="text-xs text-slate-400">Not submitted</span>}
                              </div>
                              {doc ? (
                                <>
                                  <p className="text-sm font-medium text-slate-800 truncate">{doc.document_type || doc.document_name}</p>
                                  <p className="text-xs text-slate-500 mt-0.5">{doc.document_name || doc.filename}</p>
                                  {doc.doc_status === 'rejected' && doc.doc_rejection_reason && (
                                    <p className="text-xs text-red-600 mt-1.5 font-medium bg-red-50 px-2 py-1 rounded">
                                      Rejection reason: {doc.doc_rejection_reason}
                                    </p>
                                  )}
                                  {doc.uploaded_at && (
                                    <p className="text-[10px] text-slate-400 mt-1">
                                      Uploaded: {new Date(doc.uploaded_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                    </p>
                                  )}
                                </>
                              ) : null}
                            </div>
                            <div className="flex-shrink-0">
                              {isRejected && (
                                <Button
                                  size="sm"
                                  variant={open ? 'outline' : 'default'}
                                  onClick={() => {
                                    setResubmitOpen(prev => ({ ...prev, [category]: !prev[category] }));
                                    if (!open) {
                                      setResubmitState(prev => ({
                                        ...prev,
                                        [category]: { doc_type: doc?.document_type || '', doc_name: '', file: null },
                                      }));
                                      setResubmitErrors(prev => ({ ...prev, [category]: {} }));
                                    }
                                  }}
                                  className={open ? 'border-slate-300 text-slate-600' : 'bg-blue-600 hover:bg-blue-700 text-white'}
                                  data-testid={`resubmit-toggle-${category}`}
                                >
                                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                                  {open ? 'Cancel' : 'Resubmit'}
                                </Button>
                              )}
                              {(isApproved || (!isRejected && doc)) && (
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-400 bg-slate-100 px-2.5 py-1 rounded-lg">
                                  <Check className="h-3 w-3" />
                                  {isApproved ? 'Locked' : 'Under Review'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Inline resubmit form — only for rejected doc */}
                        {isRejected && open && (
                          <div className="border border-blue-200 bg-blue-50/40 rounded-xl p-4 space-y-3">
                            <p className="text-xs font-semibold text-blue-700">
                              Resubmit {label} — upload a corrected document
                            </p>
                            <DocCategorySection
                              title={label}
                              types={types}
                              state={resubmitState[category]}
                              onChange={(val) => setResubmitState(prev => ({ ...prev, [category]: val }))}
                              error={resubmitErrors[category]}
                            />
                            <Button
                              onClick={() => handleResubmitDoc(category)}
                              disabled={submittingResubmit[category]}
                              className="w-full bg-blue-600 hover:bg-blue-700 text-white h-10"
                              data-testid={`submit-resubmit-${category}`}
                            >
                              {submittingResubmit[category] ? (
                                <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" /> Submitting...</>
                              ) : (
                                <><RefreshCw className="h-4 w-4 mr-2" /> Submit Resubmission</>
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* First-time upload form — show only when no docs submitted yet */}
              {kycData.kyc_status === 'in_process' && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-5">
                  <div className="mb-2">
                    <h3 className="text-sm font-semibold text-slate-800">Submit KYC Documents</h3>
                    <p className="text-xs text-slate-500 mt-0.5">Both sections must be filled before submitting.</p>
                  </div>


                  <DocCategorySection
                    title="ID Proof" badge="Category A"
                    types={ID_PROOF_TYPES}
                    state={idProof} onChange={setIdProof}
                    error={kycErrors.id_proof}
                  />
                  <DocCategorySection
                    title="Address Proof" badge="Category B"
                    types={ADDRESS_PROOF_TYPES}
                    state={addrProof} onChange={setAddrProof}
                    error={kycErrors.address_proof}
                  />

                  <Button
                    onClick={handleSubmitKYC}
                    disabled={submittingKYC}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11"
                    data-testid="submit-kyc-btn"
                  >
                    {submittingKYC ? (
                      <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" /> Submitting...</>
                    ) : (
                      <><ShieldCheck className="h-4 w-4 mr-2" /> Submit for Verification</>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* ══ DOCUMENTS TAB ════════════════════════════════════════════════ */}
          {activeTab === 'documents' && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
              <div className="px-6 py-4 border-b border-slate-100">
                <h2 className="font-semibold text-slate-800">Documents</h2>
                <p className="text-xs text-slate-500 mt-0.5">Manage your agreements and compliance documents</p>
              </div>
              {/* Sub-tabs */}
              <div className="px-6 border-b border-slate-100 flex gap-6">
                {[
                  { id: 'msa', label: 'MSA Agreements' },
                  { id: 'kyc_docs', label: 'KYC Documents' },
                ].map(t => (
                  <button
                    key={t.id}
                    onClick={() => setDocsSubTab(t.id)}
                    className={`py-3 text-sm font-medium border-b-2 transition-colors ${docsSubTab === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                    data-testid={`docs-subtab-${t.id}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="p-6">
                {docsSubTab === 'msa' && (
                  <MsaSection />
                )}
                {docsSubTab === 'kyc_docs' && (
                  <div className="space-y-3">
                    {submittedDocs.length === 0 ? (
                      <div className="text-center py-10">
                        <ShieldCheck className="h-10 w-10 text-slate-200 mx-auto mb-3" />
                        <p className="text-sm font-medium text-slate-500">No KYC documents submitted yet</p>
                        <p className="text-xs text-slate-400 mt-1">Submit your verification documents from the KYC tab.</p>
                      </div>
                    ) : (
                      <>
                        <p className="text-xs text-slate-500 mb-3">Your submitted KYC documents and their current review status.</p>
                        {[
                          { label: 'ID Proof', doc: latestIdProof },
                          { label: 'Address Proof', doc: latestAddrProof },
                        ].map(({ label, doc }) => (
                          <div key={label} className={`rounded-xl border p-4 ${doc?.doc_status === 'rejected' ? 'border-red-200 bg-red-50/30' : doc?.doc_status === 'approved' ? 'border-green-200 bg-green-50/20' : 'border-slate-100'}`}>
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
                              {doc && <DocStatusBadge status={doc.doc_status || 'in_process'} />}
                            </div>
                            {doc ? (
                              <>
                                <p className="text-sm font-medium text-slate-800">{doc.document_type}</p>
                                <p className="text-xs text-slate-500">{doc.document_name || doc.filename}</p>
                                {doc.doc_status === 'rejected' && doc.doc_rejection_reason && (
                                  <p className="text-xs text-red-600 mt-1.5 font-medium">Reason: {doc.doc_rejection_reason}</p>
                                )}
                                <p className="text-[10px] text-slate-400 mt-1">
                                  Submitted: {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('en-IN') : '—'}
                                </p>
                              </>
                            ) : (
                              <p className="text-xs text-slate-400">Not submitted</p>
                            )}
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── White Label Tab ──────────────────────────────────────── */}
          {activeTab === 'white_label' && (
            <div className="space-y-6">

              {/* ── Active domain banner ─────────────────────────────────── */}
              {(wlSettings?.domain_status === 'active' || wlSettings?.domain_status === 'verified') && (
                <div className="bg-green-50 border border-green-200 rounded-xl px-5 py-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-green-800">
                      {wlSettings.domain_type === 'subdomain'
                        ? `${wlSettings.subdomain}.cloudsetu.com`
                        : wlSettings.custom_domain} — Active
                    </p>
                    <p className="text-xs text-green-600 mt-0.5">Your white-label domain is live. Clients will see your branding.</p>
                  </div>
                  <a href={wlSettings.domain_type === 'subdomain'
                      ? `/public/${wlSettings.subdomain}`
                      : `https://${wlSettings.custom_domain}/public/${wlSettings.subdomain || ''}`}
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs text-green-700 font-medium hover:underline"
                    data-testid="wl-preview-link">
                    <ExternalLink className="h-3.5 w-3.5" /> Preview Storefront
                  </a>
                </div>
              )}
              {wlSettings?.domain_status === 'pending_verification' && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 text-sm text-amber-800 font-medium">
                  ⏳ DNS verification pending for <b>{wlSettings.custom_domain}</b>. Add the CNAME records below and click Verify DNS.
                </div>
              )}

              {/* ── 3 Options ────────────────────────────────────────────── */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100">
                  <h2 className="font-semibold text-slate-800">Domain Setup</h2>
                  <p className="text-xs text-slate-500 mt-0.5">Choose how your clients access your white-label portal</p>
                </div>

                {/* Option selector */}
                <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { key: 'subdomain', label: 'Free Subdomain', desc: 'mycompany.cloudsetu.com', badge: 'Free', color: 'blue' },
                    { key: 'custom', label: 'Your Own Domain', desc: 'mycompany.com', badge: 'Advanced', color: 'violet' },
                    { key: 'buy', label: 'Buy a Domain', desc: 'Search & purchase new', badge: 'Premium', color: 'amber' },
                  ].map(opt => (
                    <button key={opt.key}
                      onClick={() => { setDomainOption(opt.key); setDomainInput(''); setDomainMsg(null); setDnsRecords(null); setSearchResults(null); }}
                      data-testid={`wl-option-${opt.key}`}
                      className={`relative rounded-xl border-2 p-4 text-left transition-all ${domainOption === opt.key
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300 bg-white'}`}>
                      <span className={`absolute top-2 right-2 text-[9px] font-bold px-1.5 py-0.5 rounded-full
                        ${opt.color === 'blue' ? 'bg-blue-100 text-blue-700'
                          : opt.color === 'violet' ? 'bg-violet-100 text-violet-700'
                          : 'bg-amber-100 text-amber-700'}`}>{opt.badge}</span>
                      <p className="font-semibold text-slate-800 text-sm">{opt.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5 font-mono">{opt.desc}</p>
                    </button>
                  ))}
                </div>

                {/* ── Option 2: Subdomain ────────────────────────────────── */}
                {domainOption === 'subdomain' && (
                  <div className="px-6 pb-6 space-y-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 block mb-1.5">Enter your subdomain name</label>
                      <div className="flex gap-2">
                        <div className="flex flex-1 rounded-lg border border-slate-200 overflow-hidden">
                          <input value={domainInput}
                            onChange={e => setDomainInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                            placeholder="mycompany"
                            className="flex-1 px-3 py-2.5 text-sm outline-none"
                            data-testid="wl-subdomain-input" />
                          <span className="px-3 py-2.5 bg-slate-50 border-l border-slate-200 text-sm text-slate-500">.cloudsetu.com</span>
                        </div>
                        <Button onClick={handleSetSubdomain} disabled={domainLoading || !domainInput}
                          className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="wl-subdomain-submit">
                          {domainLoading ? 'Saving…' : 'Activate'}
                        </Button>
                      </div>
                      {domainInput && <p className="text-xs text-slate-400 mt-1">Preview: <b>{domainInput}.cloudsetu.com</b></p>}
                    </div>
                  </div>
                )}

                {/* ── Option 1: Custom Domain ────────────────────────────── */}
                {domainOption === 'custom' && (
                  <div className="px-6 pb-6 space-y-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 block mb-1.5">Enter your domain</label>
                      <div className="flex gap-2">
                        <input value={domainInput}
                          onChange={e => setDomainInput(e.target.value.toLowerCase())}
                          placeholder="mycompany.com"
                          className="flex-1 h-10 border border-slate-200 rounded-lg px-3 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                          data-testid="wl-custom-domain-input" />
                        <Button onClick={handleSetCustomDomain} disabled={domainLoading || !domainInput}
                          className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="wl-custom-domain-submit">
                          {domainLoading ? 'Saving…' : 'Get DNS Records'}
                        </Button>
                      </div>
                    </div>

                    {/* DNS Records Table */}
                    {(dnsRecords || wlSettings?.domain_status === 'pending_verification') && (
                      <div className="rounded-xl border border-slate-200 overflow-hidden">
                        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                          <p className="text-xs font-semibold text-slate-700">DNS Records to Add in Your Registrar</p>
                          <Button size="sm" variant="outline" onClick={handleVerifyDNS} disabled={verifying}
                            className="text-xs h-7" data-testid="wl-verify-dns-btn">
                            {verifying ? 'Checking…' : 'Verify DNS'}
                          </Button>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b border-slate-100">
                                {['Type','Name','Value','TTL'].map(h => (
                                  <th key={h} className="text-left px-4 py-2 text-slate-500 font-medium">{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {(dnsRecords || [
                                { type: 'CNAME', name: '@', value: 'connectdesk.cloudsetu.com', ttl: '3600' },
                                { type: 'CNAME', name: 'www', value: 'connectdesk.cloudsetu.com', ttl: '3600' },
                              ]).map((rec, i) => (
                                <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                                  <td className="px-4 py-2.5 font-mono font-bold text-violet-700">{rec.type}</td>
                                  <td className="px-4 py-2.5 font-mono">{rec.name}</td>
                                  <td className="px-4 py-2.5 font-mono text-blue-700 max-w-[220px] truncate">{rec.value}</td>
                                  <td className="px-4 py-2.5 text-slate-400">{rec.ttl}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <p className="px-4 py-2.5 text-[11px] text-slate-400">
                          DNS propagation can take 5 min – 48 hrs. Click "Verify DNS" after adding the records.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Option 3: Buy Domain ───────────────────────────────── */}
                {domainOption === 'buy' && (
                  <div className="px-6 pb-6 space-y-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 block mb-1.5">Search for a domain</label>
                      <div className="flex gap-2">
                        <input value={domainInput}
                          onChange={e => setDomainInput(e.target.value.toLowerCase())}
                          placeholder="mycompany"
                          className="flex-1 h-10 border border-slate-200 rounded-lg px-3 text-sm outline-none focus:ring-2 focus:ring-amber-500"
                          onKeyDown={e => e.key === 'Enter' && handleSearchDomains()}
                          data-testid="wl-domain-search-input" />
                        <Button onClick={handleSearchDomains} disabled={searching || !domainInput}
                          className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="wl-domain-search-btn">
                          {searching ? 'Searching…' : 'Search'}
                        </Button>
                      </div>
                    </div>
                    {searchResults && (
                      <div className="space-y-2">
                        {searchResults.length === 0
                          ? <p className="text-sm text-slate-500">No results found.</p>
                          : searchResults.map((d, i) => (
                            <div key={i} className={`flex items-center justify-between rounded-lg border px-4 py-3
                              ${d.available ? 'border-green-200 bg-green-50' : 'border-slate-200 bg-slate-50 opacity-60'}`}
                              data-testid={`domain-result-${i}`}>
                              <div>
                                <p className="text-sm font-mono font-semibold text-slate-800">{d.domainName}</p>
                                <p className={`text-xs mt-0.5 ${d.available ? 'text-green-600' : 'text-slate-400'}`}>
                                  {d.available ? '✅ Available' : '❌ Unavailable'}
                                </p>
                              </div>
                              {d.available && (
                                <div className="flex items-center gap-3">
                                  <span className="text-sm font-bold text-slate-800">₹{Number(d.price || 0).toLocaleString('en-IN')}/yr</span>
                                  <Button size="sm"
                                    onClick={() => handleBuyDomain(d.domainName, d.price)}
                                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8 gap-1"
                                    data-testid={`buy-domain-btn-${i}`}>
                                    <ShoppingCart className="w-3 h-3" />
                                    Request
                                  </Button>
                                </div>
                              )}
                            </div>
                          ))
                        }
                      </div>
                    )}
                  </div>
                )}

                {/* Message display */}
                {domainMsg && (
                  <div className={`mx-6 mb-5 rounded-lg px-4 py-3 text-sm
                    ${domainMsg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800'
                      : domainMsg.type === 'error' ? 'bg-red-50 border border-red-200 text-red-700'
                      : 'bg-blue-50 border border-blue-200 text-blue-800'}`}
                    data-testid="wl-domain-msg">
                    {domainMsg.text}
                  </div>
                )}
              </div>

              {/* ── Branding (colors, logo, pricing) ─────────────────────── */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                <div className="px-6 py-4 border-b border-slate-100">
                  <h3 className="font-semibold text-slate-800">Branding</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Customize colors and logo shown on your white-label portal</p>
                </div>
                <div className="p-6 space-y-6">
                  {/* Colors */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-slate-700">Primary Color</label>
                      <div className="flex gap-2">
                        <input type="color" value={wlForm.primary_color}
                          onChange={e => setWlForm(f => ({ ...f, primary_color: e.target.value }))}
                          className="w-10 h-10 rounded border border-slate-200 cursor-pointer" data-testid="wl-primary-color" />
                        <input type="text" value={wlForm.primary_color}
                          onChange={e => setWlForm(f => ({ ...f, primary_color: e.target.value }))}
                          className="flex-1 h-10 border border-slate-200 rounded px-3 text-sm font-mono" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-slate-700">Accent Color</label>
                      <div className="flex gap-2">
                        <input type="color" value={wlForm.accent_color}
                          onChange={e => setWlForm(f => ({ ...f, accent_color: e.target.value }))}
                          className="w-10 h-10 rounded border border-slate-200 cursor-pointer" data-testid="wl-accent-color" />
                        <input type="text" value={wlForm.accent_color}
                          onChange={e => setWlForm(f => ({ ...f, accent_color: e.target.value }))}
                          className="flex-1 h-10 border border-slate-200 rounded px-3 text-sm font-mono" />
                      </div>
                    </div>
                  </div>

                  {/* Logo */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Logo</label>
                    {wlSettings?.logo_url ? (
                      <div className="flex items-center gap-4 p-3 border border-slate-200 rounded-lg">
                        <img src={wlSettings.logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${wlSettings.logo_url}` : wlSettings.logo_url}
                          alt="Logo" className="h-12 w-12 object-contain rounded border border-slate-100" />
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => wlFileInputRef.current?.click()} disabled={wlUploading} data-testid="wl-change-logo">
                            <Upload className="h-3.5 w-3.5 mr-1.5" />Change
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={handleWlDeleteLogo} data-testid="wl-delete-logo">
                            <Trash2 className="h-3.5 w-3.5 mr-1.5" />Remove
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-3 border border-dashed border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50" onClick={() => wlFileInputRef.current?.click()}>
                        <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                          <Upload className="h-5 w-5 text-slate-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-700">{wlUploading ? 'Uploading…' : 'Upload Logo'}</p>
                          <p className="text-xs text-slate-400">PNG, JPG, GIF up to 2MB</p>
                        </div>
                      </div>
                    )}
                    <input ref={wlFileInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                      onChange={handleWlLogoUpload} className="hidden" data-testid="wl-logo-file-input" />
                  </div>

                  {/* Show pricing */}
                  <div className="flex items-center gap-3">
                    <input type="checkbox" id="wl_show_pricing" checked={wlForm.show_pricing}
                      onChange={e => setWlForm(f => ({ ...f, show_pricing: e.target.checked }))}
                      className="w-4 h-4" data-testid="wl-show-pricing" />
                    <label htmlFor="wl_show_pricing" className="text-sm text-slate-700 cursor-pointer">Show pricing on public storefront</label>
                  </div>

                  <Button onClick={handleWlSave} disabled={wlSaving} className="w-full" data-testid="wl-save-btn">
                    {wlSaving ? 'Saving…' : 'Save Branding Settings'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* ── Freebies Tab ─────────────────────────────────────────────── */}
          {activeTab === 'freebies' && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
              <Star className="h-12 w-12 text-amber-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-700 mb-2">Freebies</h2>
              <p className="text-slate-400 text-sm">Exciting features coming soon. Stay tuned!</p>
            </div>
          )}
        </div>
      </div>

      {/* Domain Request Modal */}
      <DomainRequestModal
        open={modalOpen}
        onClose={() => !modalSubmitting && setModalOpen(false)}
        domain={pendingDomain}
        years={1}
        price={pendingPrice}
        isEmailOnly={false}
        onSubmit={handleModalSubmit}
        submitting={modalSubmitting}
      />
    </div>
  );
}
